import { Modal, Button, Stack, Select, Text, Checkbox } from '@mantine/core';
import { useState, useEffect } from 'react';
import { useCostCenters } from '@api/costCenters';
import { useSetDefaultCostCenter } from '@api/companies';
import { CostCenter } from '@types';

interface Props {
  opened: boolean;
  close: () => void;
  companyId: string;
  companyName: string;
  currentCostCenterId?: number | null;
}

function getInitialCascade(
  items: CostCenter[],
  currentId: number | null | undefined,
): { l1: string | null; l2: string | null; l3: string | null } {
  if (!currentId) return { l1: null, l2: null, l3: null };
  const current = items.find((c) => c.id === currentId);
  if (!current) return { l1: null, l2: null, l3: null };

  if (current.level === 1) return { l1: String(current.id), l2: null, l3: null };
  if (current.level === 2) {
    return { l1: String(current.parent_id), l2: String(current.id), l3: null };
  }
  const l2 = items.find((c) => c.id === current.parent_id);
  return {
    l1: l2 ? String(l2.parent_id) : null,
    l2: String(current.parent_id),
    l3: String(current.id),
  };
}

function SetDefaultCostCenterModal({
  opened,
  close,
  companyId,
  companyName,
  currentCostCenterId,
}: Props) {
  const { data } = useCostCenters();
  const { mutate: setDefault, isLoading } = useSetDefaultCostCenter();

  const allItems = (data?.data ?? []).filter((c) => !c.discarded_at);

  const [selectedL1, setSelectedL1] = useState<string | null>(null);
  const [selectedL2, setSelectedL2] = useState<string | null>(null);
  const [selectedL3, setSelectedL3] = useState<string | null>(null);
  const [applyToPast, setApplyToPast] = useState(false);

  useEffect(() => {
    if (opened && data) {
      const items = data.data.filter((c) => !c.discarded_at);
      const { l1, l2, l3 } = getInitialCascade(items, currentCostCenterId);
      setSelectedL1(l1);
      setSelectedL2(l2);
      setSelectedL3(l3);
      setApplyToPast(false);
    }
  }, [opened, data, currentCostCenterId]);

  const l1Options = allItems
    .filter((c) => c.level === 1)
    .map((c) => ({ value: String(c.id), label: c.name }));

  const l2Options = allItems
    .filter((c) => c.level === 2 && String(c.parent_id) === selectedL1)
    .map((c) => ({ value: String(c.id), label: c.name }));

  const l3Options = allItems
    .filter((c) => c.level === 3 && String(c.parent_id) === selectedL2)
    .map((c) => ({ value: String(c.id), label: c.name }));

  const l1Name = allItems.find((c) => String(c.id) === selectedL1)?.name;
  const l2Name = allItems.find((c) => String(c.id) === selectedL2)?.name;

  const effectiveId = selectedL3 ?? selectedL2 ?? selectedL1;

  function handleL1Change(v: string | null) {
    setSelectedL1(v);
    setSelectedL2(null);
    setSelectedL3(null);
  }

  function handleL2Change(v: string | null) {
    setSelectedL2(v);
    setSelectedL3(null);
  }

  function handleSubmit() {
    const costCenterId = effectiveId ? Number(effectiveId) : null;
    setDefault(
      { companyId, costCenterId, applyToPastDocuments: applyToPast },
      { onSuccess: close },
    );
  }

  return (
    <Modal.Root size="sm" opened={opened} onClose={close} centered>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title fz={18} fw={600}>
            Plan de cuenta por defecto
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <Stack>
            <Text fz="sm" c="dimmed">
              Asignar plan de cuenta por defecto para <strong>{companyName}</strong>. Se aplicará
              automáticamente a los nuevos documentos de esta empresa.
            </Text>
            <Select
              label="Nivel 1"
              placeholder="Sin plan de cuenta"
              clearable
              data={l1Options}
              value={selectedL1}
              onChange={handleL1Change}
            />
            {l2Options.length > 0 && (
              <Select
                label={`Nivel 2${l1Name ? ` — ${l1Name}` : ''}`}
                placeholder="Cualquier nivel 2"
                clearable
                data={l2Options}
                value={selectedL2}
                onChange={handleL2Change}
              />
            )}
            {selectedL2 && l3Options.length > 0 && (
              <Select
                label={`Nivel 3${l2Name ? ` — ${l2Name}` : ''}`}
                placeholder="Cualquier nivel 3"
                clearable
                data={l3Options}
                value={selectedL3}
                onChange={setSelectedL3}
              />
            )}
            <Checkbox
              label="Asignar también a todos los documentos anteriores de esta empresa"
              checked={applyToPast}
              onChange={(e) => setApplyToPast(e.currentTarget.checked)}
            />
            <Button onClick={handleSubmit} loading={isLoading}>
              Guardar
            </Button>
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}

export default SetDefaultCostCenterModal;
